/**
 * OS PROVEDORES DA TELA DE CONEXÕES (Health → aba Sistema → Conexões) — o que cada linha do cartão precisa saber do
 * provedor. Espelho de `OAUTH_PROVIDER_KEYS` do backend (`modules/integrations/ProviderOAuthService.js`): provedor
 * novo lá sem linha aqui fica sem botão na tela.
 *
 * Até 08/10 a lista (Mercado Livre e Bling) e os textos estavam escritos à mão dentro do cartão, e os textos supunham
 * o formato dos dois: mandar `redirect_uri` no `start`, um exemplo de URL com o código `TG-` do Meli, "os workers
 * param de sincronizar". O TikTok Shop não tem nenhuma das três coisas.
 *
 * Cada provedor:
 *  · `sendsRedirectUri` — o `start` leva o `redirect_uri` da caixa do cabeçalho? No TikTok NÃO: o link dele não leva
 *    endereço de retorno (o TikTok devolve o navegador para a Redirect URL cadastrada no aplicativo), e a rota valida
 *    `redirect_uri` como URL — texto vazio daria 400;
 *  · `hideWhenUnconfigured` — a linha some quando o ambiente não tem as credenciais do provedor. Só o TikTok: ele
 *    exige TRÊS variáveis de ambiente e não é para aparecer nada onde elas não estão (a produção);
 *  · `tokenRenewal` — quem renova o token: `automatic` (um worker, sozinho) ou `on_use` (ninguém: só quem usa, na hora
 *    de usar — é o TikTok, que não tem worker);
 *  · `callbackPlaceholder` — o exemplo da caixa de colar;
 *  · `openStepNote` — o que acompanha o link da autorização no modal de colar;
 *  · `modalSteps` — os passos do modal de colar, atrás do accordion "Como fazer" (09/10). Cada passo é uma lista de
 *    trechos: texto, ou `{ strong: 'texto' }`;
 *  · `disconnectConsequence` — o que a confirmação de desconectar avisa;
 *  · `completionRetryHint` — o que fazer quando a conclusão falha de um jeito que só uma autorização nova resolve;
 *  · `rowNote` — uma frase fixa sobre o provedor, quando há o que dizer (09/10: vai para os detalhes da conexão — o
 *    modal —, não para a linha);
 *  · `returnMode` — como a autorização VOLTA (08/10, pedido do dono: "o padrão deve ser o link de redirect correto"):
 *    `paste` (Mercado Livre e Bling, como sempre: aba nova e a pessoa cola a URL de retorno) ou `automatic` (TikTok
 *    Shop e Shopee: Conectar leva à autorização NA MESMA ABA, o provedor devolve o navegador ao callback do backend,
 *    que o repassa a esta tela, e a linha conclui sozinha; colar vira o plano B, atrás de "Não voltou sozinho?");
 *  · `shortName` — como a frase de erro chama o provedor ("O TikTok respondeu…");
 *  · `ofLabel`, `connectedText`, `disconnectedText` — o provedor com o artigo certo ("da Shopee", "Shopee conectada.");
 *  · `registerWhere` — onde, no painel do aplicativo, se cadastra a URL de retorno que a tela mostra; e
 *    `registerDomainOnly`, quando o painel só aceita o DOMÍNIO (a Shopee) e a URL inteira mora numa variável;
 *  · `apiEnvOverrides` — o que muda com o AMBIENTE da API que o status informa (`api_env`), por ambiente: só a
 *    Shopee, que desde 10/10 abre também a produção (`live`, a loja oficial). Os campos de cima são os do teste; quem
 *    junta é `ResolveOAuthProviderForStatus` (`utils/oauthConnection.js`);
 *  · `environmentTags` — a palavra que a LINHA mostra para cada ambiente ("teste", "produção"); só a Shopee.
 */
const STANDARD_MODAL_STEPS = Object.freeze([
    Object.freeze(['Autorize por lá. Você será levado para uma página que pode dar erro — tudo bem.']),
    Object.freeze(['Copie a URL ', { strong: 'inteira' }, ' da barra do navegador daquela página e cole aqui.']),
]);
const STANDARD_CALLBACK_PLACEHOLDER = 'https://www.greycom.com.br/?code=TG-...&state=...';
const WORKERS_STOP_CONSEQUENCE = 'Os workers param de sincronizar até você reautenticar.';

/**
 * Os quatro escopos que as leituras do TikTok Shop pedem: a chave que volta em `granted_scopes` → o nome que aparece
 * no Partner Center (App & Service → Manage API) e na tela de autorização.
 */
export const TIKTOKSHOP_SCOPE_NAMES = Object.freeze({
    'seller.authorization.info': 'Shop Authorized Information',
    'seller.order.info': 'Order Information',
    'seller.fulfillment.basic': 'Fulfillment Basic',
    'seller.logistics': 'Logistics Basic',
});

export const OAUTH_PROVIDERS = Object.freeze([
    Object.freeze({
        key: 'mercadolivre',
        label: 'Mercado Livre',
        shortName: 'Mercado Livre',
        ofLabel: 'do Mercado Livre',
        connectedText: 'Mercado Livre conectado.',
        disconnectedText: 'Mercado Livre desconectado.',
        returnMode: 'paste',
        registerWhere: null,
        registerDomainOnly: null,
        sendsRedirectUri: true,
        hideWhenUnconfigured: false,
        tokenRenewal: 'automatic',
        callbackPlaceholder: STANDARD_CALLBACK_PLACEHOLDER,
        openStepNote: null,
        modalSteps: STANDARD_MODAL_STEPS,
        disconnectConsequence: WORKERS_STOP_CONSEQUENCE,
        completionRetryHint: null,
        rowNote: null,
    }),
    Object.freeze({
        key: 'bling',
        label: 'Bling',
        shortName: 'Bling',
        ofLabel: 'do Bling',
        connectedText: 'Bling conectado.',
        disconnectedText: 'Bling desconectado.',
        returnMode: 'paste',
        registerWhere: null,
        registerDomainOnly: null,
        sendsRedirectUri: true,
        hideWhenUnconfigured: false,
        tokenRenewal: 'automatic',
        callbackPlaceholder: STANDARD_CALLBACK_PLACEHOLDER,
        openStepNote: null,
        modalSteps: STANDARD_MODAL_STEPS,
        disconnectConsequence: WORKERS_STOP_CONSEQUENCE,
        completionRetryHint: null,
        rowNote: null,
    }),
    Object.freeze({
        key: 'tiktokshop',
        label: 'TikTok Shop',
        shortName: 'TikTok',
        ofLabel: 'do TikTok Shop',
        connectedText: 'TikTok Shop conectado.',
        disconnectedText: 'TikTok Shop desconectado.',
        returnMode: 'automatic',
        registerWhere: 'Partner Center → App & Service → o aplicativo → Redirect URL',
        registerDomainOnly: null,
        sendsRedirectUri: false,
        hideWhenUnconfigured: true,
        tokenRenewal: 'on_use',
        callbackPlaceholder: 'https://.../?code=...&state=...',
        openStepNote: 'Use este link: o copiado do Partner Center não serve (não leva a marca deste melidash).',
        modalSteps: Object.freeze([
            Object.freeze([
                'Entre com a conta ',
                { strong: 'PRINCIPAL' },
                ' da loja (subconta não autoriza). Confira os quatro escopos — Shop Authorized Information, Order Information, Fulfillment Basic e Logistics Basic —, escolha a região (Brasil) e o prazo, e confirme. Se aparecer aviso de conflito com outro aplicativo, ',
                { strong: 'pare' },
                '.',
            ]),
            Object.freeze([
                'Você será levado para outra página, que pode dar erro — tudo bem. Copie a URL ',
                { strong: 'inteira' },
                ' da barra do navegador daquela página e cole aqui.',
            ]),
            Object.freeze([
                'Cole em até ',
                { strong: '30 minutos' },
                ' e ',
                { strong: 'uma vez só' },
                ': o código que vem na URL vence em 30 minutos e só serve uma vez.',
            ]),
        ]),
        disconnectConsequence:
            'Apaga o token DESTE melidash. A autorização continua no TikTok até você removê-la em Seller Center → App Store → My apps and incidents.',
        completionRetryHint:
            'O código da URL vale uma vez só, por 30 minutos: clique em Conectar para autorizar de novo.',
        rowNote:
            'Nada lê o TikTok sozinho: não há worker. As leituras de estudo saem só pelo roteiro de linha de comando, com o parâmetro tiktokshop.study_reads ligado em WMS → Parâmetros.',
    }),
    Object.freeze({
        key: 'shopee',
        label: 'Shopee',
        shortName: 'Shopee',
        ofLabel: 'da Shopee',
        connectedText: 'Shopee conectada.',
        disconnectedText: 'Shopee desconectada.',
        returnMode: 'automatic',
        registerWhere: 'variável SHOPEE_REDIRECT_URL do .env do backend',
        registerDomainOnly:
            'No console da Shopee (App List → o aplicativo → Test Redirect URL Domain) o campo leva só o domínio',
        sendsRedirectUri: false,
        hideWhenUnconfigured: true,
        tokenRenewal: 'on_use',
        callbackPlaceholder: 'https://.../?code=...&shop_id=...&state=...',
        openStepNote:
            'Use este link: o botão Authorize do console da Shopee não serve (não leva a marca deste melidash).',
        modalSteps: Object.freeze([
            Object.freeze([
                'Entre com a conta da ',
                { strong: 'loja de TESTE' },
                ' (a conta real é recusada). Código de verificação: ',
                { strong: '123456' },
                '. Se o aplicativo for "Seller In House", deixe o console do Open Platform aberto neste mesmo navegador.',
            ]),
            Object.freeze([
                'Você será levado para outra página, que pode dar erro — tudo bem. Copie a URL ',
                { strong: 'inteira' },
                ' da barra do navegador daquela página e cole aqui.',
            ]),
            Object.freeze([
                'Cole em até ',
                { strong: '10 minutos' },
                ' e ',
                { strong: 'uma vez só' },
                ': o código que vem na URL vence em 10 minutos e só serve uma vez.',
            ]),
        ]),
        disconnectConsequence: 'Apaga o token DESTE melidash. A autorização continua na Shopee até você cancelá-la.',
        completionRetryHint: 'O código vale uma vez só, por 10 minutos: clique em Conectar para autorizar de novo.',
        rowNote:
            'Nada lê a Shopee sozinho: não há worker. As leituras de estudo saem só pelo roteiro de linha de comando, com o parâmetro shopee.study_reads ligado em WMS → Parâmetros.',
        environmentTags: Object.freeze({ sandbox: 'teste', live: 'produção' }),
        apiEnvOverrides: Object.freeze({
            // A PRODUÇÃO (10/10): a loja OFICIAL. O aplicativo é "Seller In House System": a autorização exige o
            // console do Open Platform logado no mesmo navegador (https://open.shopee.com/faq/107); subconta não entra.
            live: Object.freeze({
                registerDomainOnly:
                    'No console da Shopee (App List → o aplicativo → Live Redirect URL Domain) o campo leva só o domínio',
                modalSteps: Object.freeze([
                    Object.freeze([
                        'Deixe o ',
                        { strong: 'console do Open Platform' },
                        ' aberto neste mesmo navegador. Entre com a conta da ',
                        { strong: 'loja oficial' },
                        ' (subconta não entra; conta principal: "Switch to Sub Account") e confirme.',
                    ]),
                    Object.freeze([
                        'Você será levado para outra página, que pode dar erro — tudo bem. Copie a URL ',
                        { strong: 'inteira' },
                        ' da barra do navegador daquela página e cole aqui.',
                    ]),
                    Object.freeze([
                        'Cole em até ',
                        { strong: '10 minutos' },
                        ' e ',
                        { strong: 'uma vez só' },
                        ': o código que vem na URL vence em 10 minutos e só serve uma vez.',
                    ]),
                ]),
            }),
        }),
    }),
]);

/**
 * Os erros da conclusão (`POST /integrations/oauth/:provider/complete`) que só uma AUTORIZAÇÃO NOVA resolve: a troca
 * do código foi tentada (ele vale uma vez), a janela de 30 minutos venceu, ou a loja recusou. A tela decide pelo
 * CÓDIGO do erro, nunca pelo texto.
 */
export const OAUTH_COMPLETION_RETRY_CODES = Object.freeze([
    'INT26_OAUTH_EXCHANGE_FAIL',
    'INT26_OAUTH_STATE_INVALID',
    'INT26_OAUTH_PROVIDER_REFUSED',
    // O `state` é de outro usuário (quem conclui tem de ser quem clicou em Conectar): só um Conectar novo, deste usuário.
    'INT26_OAUTH_STATE_OTHER_USER',
]);

/**
 * Onde a tela guarda, NESTA ABA (`sessionStorage`), o `state` do Conectar que saiu para a autorização na mesma aba:
 * `melidash.oauth.pending.<provedor>`. A volta automática só conclui com essa pendência (ver `ResolveOAuthReturn`).
 */
export const OAUTH_PENDING_STORAGE_PREFIX = 'melidash.oauth.pending.';

/** O parâmetro e o fragmento com que o callback do backend devolve o navegador a `/health` (`routes/integrations.js`). */
export const OAUTH_RETURN_PROVIDER_PARAM = 'oauth_provider';
export const OAUTH_RETURN_FRAGMENT = '#oauth_return=';

/** O `state` que o `complete` aceita no corpo: texto de 1 a 1.024 caracteres. */
export const OAUTH_STATE_MAX_LENGTH = 1024;
